// campaigns.ts — Patch MCC-6a. Email-campaigns client: templates, campaigns,
// the prepare/approve state machine, and the recipient-snapshot detail. The
// smart-list picker reuses the 4c saved lists. No send happens here (6c).

import { request } from './request'

export type CampaignStatus =
  | 'draft'
  | 'review'
  | 'scheduled'
  | 'sending'
  | 'paused'
  | 'sent'
  | 'cancelled'

export interface CampaignListRow {
  id: string
  name: string
  status: CampaignStatus
  from_identity: string | null
  subject_snapshot: string | null
  template_id: string | null
  smart_list_id: string | null
  recipient_count: number
  approved_at: string | null
  scheduled_at: string | null
  created_at: string
  updated_at: string | null
}

export interface CampaignDetail extends CampaignListRow {
  body_text_snapshot: string | null
  body_html_snapshot: string | null
  rule_json: string | null
  approved_by_user_id: string | null
  last_throttled_at?: string | null
}

export interface RecipientSample {
  id: string
  profile_id: string
  email_snapshot: string
  status: string
  send_record_id: string | null
  sent_at: string | null
  last_error: string | null
}

export interface CampaignSendMetrics {
  by_status: Record<string, number>
  opened: number
  clicked: number
}

export interface CampaignDetailResponse {
  ok: boolean
  campaign: CampaignDetail
  recipients_by_status: Record<string, number>
  recipient_sample: RecipientSample[]
  send_metrics?: CampaignSendMetrics
}

export interface RunSendBatchResult {
  ok: boolean
  ran: boolean
  reason?: string
  attempted: number
  sent: number
  failed: number
  suppressed: number
  unknown: number
  throttled: boolean
  daily_cap: number
  sent_today: number
  stale_reset: number
  stale_reconciled: number
}

export interface EmailTemplateRow {
  id: string
  name: string
  subject: string | null
  body_text: string | null
  body_html: string | null
  voice_profile_key: string | null
  status: string
}

export interface CreateCampaignInput {
  name: string
  smart_list_id?: string | null
  rule?: unknown
  template_id?: string | null
  from_identity?: string | null
  subject?: string | null
  body_text?: string | null
  body_html?: string | null
}

export interface SmartListLite {
  id: string
  name: string
  count: number | null
}

export const campaignsApi = {
  list: () => request<{ ok: boolean; campaigns: CampaignListRow[] }>('/api/admin/campaigns'),

  get: (id: string) => request<CampaignDetailResponse>(`/api/admin/campaigns/${id}`),

  create: (input: CreateCampaignInput) =>
    request<{ ok: boolean; id: string }>('/api/admin/campaigns', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  patch: (id: string, input: Partial<CreateCampaignInput>) =>
    request<{ ok: boolean }>(`/api/admin/campaigns/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),

  // MCC-6b: AI-draft this draft campaign's subject + body for its audience.
  // Returns a suggestion only; the human applies it via patch() (draft-only).
  draftContent: (id: string, input: { audience: string; goal?: string }) =>
    request<{ ok: boolean; subject: string; body_text: string; audience: string; model: string }>(
      `/api/admin/campaigns/${id}/draft-content`,
      { method: 'POST', body: JSON.stringify(input) },
    ),

  prepare: (id: string) =>
    request<{ ok: boolean; status: string; recipient_count: number }>(
      `/api/admin/campaigns/${id}/prepare`,
      { method: 'POST' },
    ),

  approve: (id: string) =>
    request<{ ok: boolean; status: string }>(`/api/admin/campaigns/${id}/approve`, {
      method: 'POST',
    }),

  // MCC-6c: run the send worker. campaign_id targets one campaign ("send now");
  // omit it to drain every scheduled campaign. Bounded by the warm-up daily cap.
  runSendBatch: (input?: { campaign_id?: string; max?: number }) =>
    request<RunSendBatchResult>('/api/admin/campaigns/run-send-batch', {
      method: 'POST',
      body: JSON.stringify(input ?? {}),
    }),

  revise: (id: string) =>
    request<{ ok: boolean; status: string }>(`/api/admin/campaigns/${id}/revise`, {
      method: 'POST',
    }),

  cancel: (id: string) =>
    request<{ ok: boolean; status: string }>(`/api/admin/campaigns/${id}/cancel`, {
      method: 'POST',
    }),

  remove: (id: string) =>
    request<{ ok: boolean; removed: boolean }>(`/api/admin/campaigns/${id}`, {
      method: 'DELETE',
    }),

  templates: () =>
    request<{ ok: boolean; templates: EmailTemplateRow[] }>('/api/admin/email-templates'),

  createTemplate: (input: { name: string; subject?: string; body_text?: string; body_html?: string; voice_profile_key?: string }) =>
    request<{ ok: boolean; id: string }>('/api/admin/email-templates', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  // smart lists, for the campaign targeting picker (reuses the 4c surface).
  smartLists: () =>
    request<{ ok: boolean; smart_lists: SmartListLite[] }>('/api/admin/smart-lists'),
}
